import { useCandidateEmails } from '@/hooks/use-candidate-emails.ts'
import { useAuth } from '@/lib'
import type { CandidateModel } from '@/ui'
import { CheckCheckIcon, MailIcon, SendIcon } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmailComposer } from './email-composer'
import { EmailItem } from './email-item'
import type { ComposeMode, Email, EmailContact } from './types'
import { DUMMY_RECRUITER, mapApiEmailToEmail } from './types'

export type EmailThreadProps = {
	candidate: CandidateModel
	campaignId?: string
	selectedEmailId?: string | null
	getEmailUrl?: (emailId: string) => string
}

export function EmailThread({
	candidate,
	campaignId,
	selectedEmailId,
	getEmailUrl,
}: EmailThreadProps) {
	const { t } = useTranslation('common')
	const { user } = useAuth()
	const { emails: apiEmails, sendEmail, markAsRead, markAsUnread, markAllAsRead } = useCandidateEmails(
		candidate.id,
		campaignId,
	)
	const listRef = useRef<HTMLDivElement>(null)

	// Get recruiter info from WorkOS user, fallback to dummy
	const recruiter: EmailContact = useMemo(() => {
		if (user) {
			const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null
			return {
				name: fullName ?? user.email ?? 'Recruiter',
				email: user.email ?? 'recruiter@company.com',
			}
		}
		return DUMMY_RECRUITER
	}, [user])

	const candidateContact: EmailContact = useMemo(
		() => ({
			name: candidate.name,
			email: candidate.email,
		}),
		[candidate.name, candidate.email],
	)

	// Map API emails to frontend Email type, sorted chronologically (oldest first)
	const emails: Email[] = useMemo(
		() =>
			apiEmails
				.map((apiEmail) => mapApiEmailToEmail(apiEmail, candidateContact, recruiter))
				.sort((a, b) => a.sentAt.getTime() - b.sentAt.getTime()),
		[apiEmails, candidateContact, recruiter],
	)

	// Scroll to selected email when URL changes
	useEffect(() => {
		if (selectedEmailId && listRef.current) {
			const element = document.getElementById(`email-${selectedEmailId}`)
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}
	}, [selectedEmailId])

	const [isComposing, setIsComposing] = useState(false)
	const [composeMode, setComposeMode] = useState<ComposeMode>('new')
	const [replyToEmail, setReplyToEmail] = useState<Email | null>(null)

	const emailsById = useMemo(
		() => new Map(emails.map((email) => [email.id, email])),
		[emails],
	)

	const handleReply = useCallback((emailId: string) => {
		const email = emailsById.get(emailId)
		if (!email) { return }

		setReplyToEmail(email)
		setComposeMode('reply')
		setIsComposing(true)

		setTimeout(() => {
			const element = document.getElementById(`email-${emailId}`)
			element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}, 100)
	}, [emailsById])

	const handleToggleRead = useCallback((emailId: string) => {
		const email = emailsById.get(emailId)
		if (!email) { return }

		if (email.isRead) {
			void markAsUnread([emailId])
		} else {
			void markAsRead([emailId])
		}
	}, [emailsById, markAsRead, markAsUnread])

	const handleEmailClick = useCallback((emailId: string) => {
		const email = emailsById.get(emailId)
		if (!email) {
			return
		}

		if (!email.isRead && email.isFromCandidate) {
			void markAsRead([emailId])
		}
	}, [emailsById, markAsRead])

	const handleCompose = useCallback(() => {
		setReplyToEmail(null)
		setComposeMode('new')
		setIsComposing(true)
	}, [])

	const handleCancelCompose = useCallback(() => {
		setIsComposing(false)
		setReplyToEmail(null)
	}, [])

	const handleSendEmail = useCallback(
		async (emailData: Omit<Email, 'id' | 'sentAt' | 'isRead' | 'isFromCandidate' | 'attachments' | 'readBy'>) => {
			await sendEmail({
				subject: emailData.subject,
				body: emailData.body,
			})

			setIsComposing(false)
			setReplyToEmail(null)
		},
		[sendEmail],
	)

	const unreadCount = useMemo(
		() => emails.filter((e) => !e.isRead && e.isFromCandidate).length,
		[emails],
	)

	return (
		<div className="flex flex-col h-full bg-white">
			{/* Header */}
			<div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0 bg-gray-50/50">
				<div className="flex items-center gap-3">
					<div className="flex items-center gap-2">
						<div className="size-8 rounded-full bg-brand-100 flex items-center justify-center">
							<MailIcon className="size-4 text-brand-600" />
						</div>
						<h2 className="font-semibold text-gray-900">{t('emailThread.title')}</h2>
					</div>
					{unreadCount > 0 && (
						<span className="px-2.5 py-1 text-xs font-medium bg-brand-100 text-brand-700 rounded-full">
							{t('emailThread.newCount', { count: unreadCount })}
						</span>
					)}
				</div>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={() => void markAllAsRead()}
						disabled={unreadCount === 0}
						className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
					>
						<CheckCheckIcon className="size-4" />
						<span className="hidden sm:inline">
							{unreadCount === 0 ? t('emailThread.allRead') : t('emailThread.markRead')}
						</span>
					</button>
					<button
						type="button"
						onClick={handleCompose}
						disabled={isComposing}
						className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<SendIcon className="size-4" />
						{t('emailThread.compose')}
					</button>
				</div>
			</div>

			{/* Email list */}
			<div ref={listRef} className="flex-1 overflow-auto">
				{emails.length === 0
					? (
						<div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
							<MailIcon className="size-12 mb-3 opacity-50" />
							<p className="text-sm">{t('emailThread.empty')}</p>
						</div>
					)
					: (
						<div>
							{emails.map((email) => (
								<EmailItem
									key={email.id}
									email={email}
									isHighlighted={email.id === selectedEmailId}
									isBeingRepliedTo={replyToEmail?.id === email.id}
									onReply={handleReply}
									onToggleRead={handleToggleRead}
									onClick={handleEmailClick}
									emailUrl={getEmailUrl?.(email.id)}
								/>
							))}
						</div>
					)}
			</div>

			{/* Composer */}
			<AnimatePresence>
				{isComposing && (
					<EmailComposer
						mode={composeMode}
						recruiter={recruiter}
						replyToEmail={replyToEmail ?? undefined}
						candidateContact={candidateContact}
						onSend={handleSendEmail}
						onCancel={handleCancelCompose}
					/>
				)}
			</AnimatePresence>
		</div>
	)
}
